import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import uploadSvg from 'assets/icons/upload.svg';
import errorSvg from 'assets/icons/error.svg';
import usePendoTracking from 'customHooks/usePendoTracking';
import { processPastedTextToArray } from 'utils/processPasteToArray';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import Alert from 'lib/Alert';
import { AlertThemeOption } from 'lib/Alert/Alert.types';
import Button, { Align, Color } from 'lib/Button';
import { FrameFilePasteAreaProps, PasteResult } from './FrameFilePasteArea.types';
import FailedFramesModal from './FailedFramesModal/FailedFramesModal';

const handlePastedText = (text: string, bulkPastedFrameCodes: RouteFrameCode[], environmentId: string): PasteResult => {
  const arrayFromText = processPastedTextToArray(text);
  const acceptedFrames: RouteFrameCode[] = [];
  const rejectedFrames: RouteFrameCode[] = [];

  const uniqueFramesArray: string[] = [...new Set(arrayFromText)];

  uniqueFramesArray.forEach((frame) => {
    const isAccepted = bulkPastedFrameCodes.length ? bulkPastedFrameCodes.some((code) => code.name !== frame) : true;

    if (isAccepted) {
      acceptedFrames.push({ name: frame, code: frame, mediaOwner: environmentId, include: true });
    } else {
      rejectedFrames.push({ name: frame, code: frame, mediaOwner: environmentId, include: false });
    }
  });

  return { acceptedFrames, rejectedFrames };
};

const FrameFilePasteArea = ({
  environmentId,
  selectedRouteFrameCodes,
  setSelectedRouteFrameCodes,
}: FrameFilePasteAreaProps): React.ReactElement => {
  const { getPendoDataId } = usePendoTracking();
  const pasteZone = useRef<HTMLDivElement>(null);

  const [isActive, setIsActive] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isFailedFramesModalOpen, setIsFailedFramesModalOpen] = useState(false);
  const [acceptedIDs, setAcceptedIDs] = useState<RouteFrameCode[]>([]);
  const [rejectedIDs, setRejectedIDs] = useState<RouteFrameCode[]>([]);

  useEffect(() => {
    if (pasteZone && pasteZone.current) {
      pasteZone.current.addEventListener('paste', (event) => {
        event.preventDefault();
        const text = event.clipboardData?.getData('text');

        const { acceptedFrames, rejectedFrames } = handlePastedText(
          text ?? '',
          selectedRouteFrameCodes ?? [],
          environmentId,
        );

        setAcceptedIDs(acceptedFrames);
        setRejectedIDs(rejectedFrames);

        const pendoDataId = getPendoDataId('Bulk paste frame IDs');
        window.pendo?.track(pendoDataId);
      });
    }
  }, []);

  useEffect(() => {
    if (acceptedIDs.length === 0) return;

    const filteredAcceptedIDs = acceptedIDs.filter(
      (id) => !selectedRouteFrameCodes.some((code) => code.name === id.name),
    );
    const filteredRejectedIDs = acceptedIDs.filter((id) =>
      selectedRouteFrameCodes.some((code) => code.name === id.name),
    );

    const newRouteFrameCodes = selectedRouteFrameCodes.concat(filteredAcceptedIDs);

    setSelectedRouteFrameCodes(newRouteFrameCodes);
    setRejectedIDs(filteredRejectedIDs);
    if (filteredRejectedIDs.length > 0) {
      setIsAlertOpen(true);
    }
  }, [acceptedIDs]);

  const handleCloseAlert = (): void => {
    setIsAlertOpen(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        className={cx('flex flex-col border rounded-md border-primary-600 text-primary-600', {
          'border-solid bg-primary-600-opacity-5': isActive,
          'border-dashed': !isActive,
        })}
        ref={pasteZone}
      >
        <Button
          color={Color.TRANSPARENT}
          align={Align.START}
          label={isActive ? 'Paste frame IDs' : 'Click to bulk paste frame IDs'}
          onFocus={() => setIsActive(true)}
          onBlur={() => setIsActive(false)}
          svg={uploadSvg}
          svgClassnames="fill-current w-4 h-4"
          classNames="p-2 relative flex w-full gap-1 disabled:cursor-not-allowed"
        />
      </div>
      {rejectedIDs.length > 0 && isAlertOpen && (
        <Alert
          header="Frame IDs not added"
          theme={AlertThemeOption.PINK_RED}
          icon={errorSvg}
          onClose={handleCloseAlert}
        >
          <div className="body-base">
            <span>{`${rejectedIDs.length} of ${acceptedIDs.length} frame IDs not added, as they already exist. `}</span>
            <span
              className="cursor-pointer underline"
              role="presentation"
              onClick={() => setIsFailedFramesModalOpen(true)}
            >
              View failed frames.
            </span>
          </div>
        </Alert>
      )}
      <FailedFramesModal
        isOpen={isFailedFramesModalOpen}
        onClose={() => setIsFailedFramesModalOpen(false)}
        rejectedFrames={rejectedIDs}
      />
    </div>
  );
};

export default FrameFilePasteArea;
